import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Numbers as NumbersIcon,
  MedicalServices as MedicalServicesIcon,
  LocationOn as LocationOnIcon,
  Science as ScienceIcon,
  NoteAdd as NoteAddIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../utils/authUtils';
import { createApiUrl } from '../utils/apiConfig';

const ACCENT = 'var(--rd-primary)';

const KpiCard = ({ value, label, color }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 1,
      borderRadius: 1.5,
      bgcolor: color,
      color: 'var(--rd-primary-contrast)',
      minWidth: 0,
      flex: 1
    }}
  >
    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value ?? '–'}</Typography>
    <Typography variant="caption" sx={{ lineHeight: 1.2, textAlign: 'center', mt: 0.25, opacity: 0.92 }}>{label}</Typography>
  </Box>
);

const BarRow = ({ label, value, maxValue, color }) => {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
      <Typography variant="caption" noWrap sx={{ width: 70, flexShrink: 0, textAlign: 'right', fontWeight: 500 }}>{label}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 5 }
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ width: 36, flexShrink: 0, fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
};

const CHART_COLORS = Array.from({ length: 10 }, (_, i) => `var(--rd-chart-${i + 1})`);

const PersonalStatsWidget = ({ widgetBodyHeight }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const [casesData, setCasesData] = useState(null);
  const [modalitiesData, setModalitiesData] = useState(null);
  const [regionsData, setRegionsData] = useState(null);
  const [studiesData, setStudiesData] = useState(null);
  const [addendaData, setAddendaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      const headers = { ...getAuthHeaders() };
      try {
        const [casesRes, modRes, regRes, stuRes, addRes] = await Promise.allSettled([
          fetch(createApiUrl('statistics/personal/fallzahlen'), { headers }),
          fetch(createApiUrl('statistics/personal/modalities'), { headers }),
          fetch(createApiUrl('statistics/personal/regions'), { headers }),
          fetch(createApiUrl('statistics/personal/studies'), { headers }),
          fetch(createApiUrl('statistics/personal/addenda'), { headers })
        ]);
        if (cancelled) return;
        if (casesRes.status === 'fulfilled' && casesRes.value.ok) setCasesData(await casesRes.value.json());
        if (modRes.status === 'fulfilled' && modRes.value.ok) setModalitiesData((await modRes.value.json()).modalities);
        if (regRes.status === 'fulfilled' && regRes.value.ok) setRegionsData((await regRes.value.json()).regions);
        if (stuRes.status === 'fulfilled' && stuRes.value.ok) setStudiesData((await stuRes.value.json()).studies);
        if (addRes.status === 'fulfilled' && addRes.value.ok) setAddendaData((await addRes.value.json()).addenda);
      } catch (e) {
        console.error('PersonalStatsWidget fetch error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const aggregateTotals = (entityData) => {
    if (!entityData) return [];
    return Object.entries(entityData)
      .map(([name, stats]) => ({
        name,
        total: (stats.my_reports?.total_count || 0) +
               (stats.foreign_signed_reports?.total_count || 0) +
               (stats.indication_approvals?.total_count || 0) +
               (stats.released_reports?.total_count || 0)
      }))
      .sort((a, b) => b.total - a.total);
  };

  const modalitySummary = useMemo(() => aggregateTotals(modalitiesData), [modalitiesData]);
  const regionSummary = useMemo(() => aggregateTotals(regionsData), [regionsData]);

  const studySummary = useMemo(() => {
    if (!studiesData) return [];
    return Object.entries(studiesData)
      .map(([name, stats]) => ({
        name,
        total: (stats.my_reports?.total_count || 0) +
               (stats.foreign_signed_reports?.total_count || 0) +
               (stats.indication_approvals?.total_count || 0) +
               (stats.released_reports?.total_count || 0)
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [studiesData]);

  const tabs = [
    { icon: <NumbersIcon sx={{ fontSize: 16 }} />, label: t('statistics.personal.categories.fallzahlen', 'Fallzahlen') },
    { icon: <MedicalServicesIcon sx={{ fontSize: 16 }} />, label: t('statistics.personal.categories.modalitaeten', 'Modalitäten') },
    { icon: <LocationOnIcon sx={{ fontSize: 16 }} />, label: t('statistics.personal.categories.regionen', 'Regionen') },
    { icon: <ScienceIcon sx={{ fontSize: 16 }} />, label: t('statistics.personal.categories.studienart', 'Studienart') },
    { icon: <NoteAddIcon sx={{ fontSize: 16 }} />, label: t('statistics.personal.categories.addenda', 'Addenda') }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
      </Box>
    );
  }

  const renderCases = () => {
    if (!casesData) return <NoData />;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <KpiCard
            value={casesData.indication_approvals?.total_count}
            label={t('statistics.personal.fallzahlen.indicationApprovals', 'Indikationsprüfungen')}
            color="var(--rd-chart-1)"
          />
          <KpiCard
            value={casesData.my_reports?.total_count}
            label={t('statistics.personal.fallzahlen.myReports', 'Meine Befunde')}
            color="var(--rd-chart-2)"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <KpiCard
            value={casesData.foreign_signed_reports?.total_count}
            label={t('statistics.personal.fallzahlen.foreignSignedReports', '(Fremd-)signiert')}
            color="var(--rd-chart-3)"
          />
          <KpiCard
            value={casesData.released_reports?.total_count}
            label={t('statistics.personal.fallzahlen.releasedReports', 'Freigegeben')}
            color="var(--rd-chart-4)"
          />
        </Box>
      </Box>
    );
  };

  const renderBarList = (summary, emptyKey) => {
    if (!summary || summary.length === 0) return <NoData />;
    const max = Math.max(...summary.map(s => s.total), 1);
    return (
      <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
        {summary.map((item, i) => (
          <BarRow
            key={item.name}
            label={item.name}
            value={item.total}
            maxValue={max}
            color={CHART_COLORS[i % CHART_COLORS.length]}
          />
        ))}
      </Box>
    );
  };

  const renderAddenda = () => {
    if (!addendaData) return <NoData />;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.5, p: 1.5 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: 'var(--rd-chart-1)' }}>
          {addendaData.total_count ?? 0}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.personal.addenda.myAddenda', 'Meine Addenda')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('statistics.personal.addenda.total', 'Gesamt')}
        </Typography>
      </Box>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 0: return renderCases();
      case 1: return renderBarList(modalitySummary);
      case 2: return renderBarList(regionSummary);
      case 3: return renderBarList(studySummary);
      case 4: return renderAddenda();
      default: return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            px: 1.5,
            fontSize: '0.75rem',
            textTransform: 'none',
            minWidth: 'auto'
          },
          '& .Mui-selected': { color: ACCENT },
          '& .MuiTabs-indicator': { backgroundColor: ACCENT }
        }}
      >
        {tabs.map((tab, i) => (
          <Tab key={i} icon={tab.icon} label={tab.label} iconPosition="start" />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

const NoData = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('statistics.personal.fallzahlen.noData', 'Keine Daten verfügbar')}
      </Typography>
    </Box>
  );
};

PersonalStatsWidget.displayName = 'PersonalStatsWidget';

export default PersonalStatsWidget;
